import React from 'react';
import { AlertCircle, TrendingDown, Minus, Info, ArrowRight, TrendingUp } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setIsPreFiltered, setPreDefinedFilter, setPreDefinedSort } from '../../redux/slices/pricingSlice';
import { useGetPricingActionsQuery } from '../../redux/slices/apiSlice';
import { ColorRing } from 'react-loader-spinner';

// ── Tag display config ────────────────────────────────────────────────────────
const TAG_CONFIG = {
  hold: {
    label: 'Hold',
    description: 'Current pricing is optimal',
    Icon: Minus,
    accentColor: '#9ca3af',
    cardBg: 'bg-gray-50',
    headerColor: 'text-gray-700',
    descColor: 'text-gray-500',
    iconColor: 'text-gray-500',
    badgeBg: 'bg-gray-200 text-gray-600',
  },
  pull: {
    label: 'Pull / Liquidate',
    description: 'Immediate action required - review listing details, videos, recut, recert, etc.',
    Icon: AlertCircle,
    accentColor: '#ef4444',
    cardBg: 'bg-red-50',
    headerColor: 'text-red-600',
    descColor: 'text-red-500',
    iconColor: 'text-red-500',
    badgeBg: 'bg-red-100 text-red-700',
  },
  aggressive_cut: {
    label: 'Aggressive Cut',
    description: 'Price reduction of 2-4% recommended',
    Icon: TrendingDown,
    accentColor: '#fb923c',
    cardBg: 'bg-orange-50',
    headerColor: 'text-orange-600',
    descColor: 'text-orange-500',
    iconColor: 'text-orange-500',
    badgeBg: 'bg-orange-100 text-orange-700',
  },
  medium_cut: {
    label: 'Medium Cut',
    description: 'Price reduction of 1-2% recommended',
    Icon: TrendingDown,
    accentColor: '#f59e0b',
    cardBg: 'bg-amber-50',
    headerColor: 'text-amber-700',
    descColor: 'text-amber-600',
    iconColor: 'text-amber-600',
    badgeBg: 'bg-amber-100 text-amber-700',
  },
  small_cut: {
    label: 'Small Cut',
    description: 'Price reduction of 0.5-1.0% recommended',
    Icon: Minus,
    accentColor: '#facc15',
    cardBg: 'bg-yellow-50',
    headerColor: 'text-yellow-700',
    descColor: 'text-yellow-600',
    iconColor: 'text-yellow-500',
    badgeBg: 'bg-yellow-100 text-yellow-700',
  },
  review_criteria: {
    label: 'Review Criteria',
    description: 'Review the filter criteria / competitions as there are very few to compare against',
    Icon: Info,
    accentColor: '#60a5fa',
    cardBg: 'bg-blue-50',
    headerColor: 'text-blue-600',
    descColor: 'text-blue-500',
    iconColor: 'text-blue-500',
    badgeBg: 'bg-blue-100 text-blue-700',
  },
};

const DEFAULT_TAG_CONFIG = {
  label: 'Pricing Action',
  description: '',
  Icon: TrendingUp,
  accentColor: '#d1d5db',
  cardBg: 'bg-gray-50',
  headerColor: 'text-gray-700',
  descColor: 'text-gray-500',
  iconColor: 'text-gray-500',
  badgeBg: 'bg-gray-100 text-gray-600',
};


// ── Helpers ───────────────────────────────────────────────────────────────────
function extractGrade(bucket) {
  if (!bucket) return null;
  const m = bucket.match(/\(([a-zA-Z][+\-]?)\)/);
  return m ? m[1].toUpperCase() : null;
}

function fmtPrice(val) {
  if (val == null) return '—';
  return '$' + Number(val).toLocaleString();
}

function fmtValue(val) {
  if (val == null || val === 0) return '—';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${Math.round(val / 1_000)}K`;
  return `$${Number(val).toLocaleString()}`;
}

function stoneDisplayId(stone) {
  const sn = stone.stock_number || '';
  if (sn.includes('|') || sn.length > 20) return `#${stone.item_id}`;
  return sn || `#${stone.item_id}`;
}

// ── Stone mini-card ───────────────────────────────────────────────────────────
function StoneCard({ stone, tagCfg }) {
  const grade = extractGrade(stone.stock_performance_data?.bucket);
  const countChange = stone.last_n_days_count_change;
  const rankChange = stone.last_n_days_rank_change;
  const marketCount = stone.pricing_market_item_count;

  return (
    <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
      {/* Stock number + grade badge */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-blue-600 truncate">
          {stoneDisplayId(stone)}
        </span>
        {grade && (
          <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ml-2 shrink-0 ${tagCfg.badgeBg}`}>
            {grade}
          </span>
        )}
      </div>

      {/* 2-col specs */}
      <div className="grid grid-cols-2 gap-x-2 text-[11px] mb-1">
        <div className="text-gray-400">Shape: <span className="text-gray-800 font-semibold">{stone.shape || '—'}</span></div>
        <div className="text-gray-400">Carat: <span className="text-gray-800 font-semibold">{stone.carats ? `${stone.carats}ct` : '—'}</span></div>
        <div className="text-gray-400">Color: <span className="text-gray-800 font-semibold">{stone.color || '—'}</span></div>
        <div className="text-gray-400">Clarity: <span className="text-gray-800 font-semibold">{stone.clarity || '—'}</span></div>
      </div>

      {/* Price */}
      <div className="text-[11px] text-gray-400 mb-1">
        Price: <span className="text-gray-900 font-semibold">{fmtPrice(stone.updated_price)}</span>
      </div>

      {/* Rank + Count */}
      <div className="flex items-center gap-4 text-[11px] text-gray-400">
        <span>
          Rank: <span className="text-gray-700 font-semibold">{marketCount ?? '—'}</span>
          {rankChange != null && rankChange !== 0 && (
            <span className={`ml-1 font-semibold ${rankChange > 0 ? 'text-red-500' : 'text-green-600'}`}>
              {rankChange > 0 ? `+${rankChange}` : rankChange} ↘
            </span>
          )}
        </span>
        <span>
          Count: <span className="text-gray-700 font-semibold">{marketCount ?? '—'}</span>
          {countChange != null && countChange !== 0 && (
            <span className={`ml-1 font-semibold ${countChange < 0 ? 'text-red-500' : 'text-green-600'}`}>
              {countChange > 0 ? `+${countChange}` : countChange}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// ── Action card ───────────────────────────────────────────────────────────────
function ActionCard({ item }) {
  const cfg = TAG_CONFIG[item.tag] ?? { ...DEFAULT_TAG_CONFIG, label: item.name };
  const { Icon } = cfg;
  const stones = (item.sample_stones || []).slice(0, 3);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleViewAll = () => {
    const filters = item.criteria?.filters;
    if (!filters) return;
    dispatch(setIsPreFiltered(true));
    dispatch(setPreDefinedFilter(filters));
    const multiSort = item.criteria?.sort?.multi_sort;
    if (multiSort) {
      dispatch(setPreDefinedSort(multiSort));
    }
    navigate('/price_co-pilot?page=0');
  };

  return (
    <div className={`bg-white rounded-2xl border  border-[${cfg.accentColor}] flex flex-col overflow-hidden`}>

      {/* Header: colored tinted bg, only this section */}
      <div className={`${cfg.cardBg} px-4 pt-3 pb-2`}>
        <div className="flex items-center gap-1.5">
          <Icon className={`w-3.5 h-3.5 shrink-0 ${cfg.iconColor}`} />
          <p className={`text-sm font-bold ${cfg.headerColor}`}>{cfg.label}</p>
        </div>
        {cfg.description && (
          <p className={`text-[10px] leading-snug ${cfg.descColor}`}>{cfg.description}</p>
        )}
      </div>

      {/* Stats */}
      <div className="px-4 pt-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Total Stones</p>
          <p className="text-xl font-bold text-gray-900">{item.count?.toLocaleString() ?? '—'}</p>
        </div>
        <div>
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Total Value</p>
          <p className="text-xl font-bold text-gray-900">{fmtValue(item.total_value)}</p>
        </div>
      </div>

      {/* Sample stones section */}
      {stones.length > 0 && (
        <>
          <div className="border-t border-gray-100" />
          <div className="px-4 pt-2.5 pb-3 flex-1 flex flex-col">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
              Sample Stones
            </p>
            <div className="flex flex-col gap-1.5 flex-1">
              {stones.map((s) => (
                <StoneCard key={s.item_id} stone={s} tagCfg={cfg} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* View All button */}
      <div className="px-4 pb-4">
        <button
          className="w-full flex items-center justify-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold py-2 rounded-lg transition-colors"
          onClick={handleViewAll}
        >
          View All {item.count?.toLocaleString()} Stones
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function PricingActions({ accountDetails }) {
  const accountId = accountDetails?.account_id;
  const { data, isLoading, isError } = useGetPricingActionsQuery(accountId, {
    skip: !accountId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <ColorRing visible height={48} width={48} colors={['#4A90D9', '#4A90D9', '#4A90D9', '#4A90D9', '#4A90D9']} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center text-gray-500 py-20 text-sm">
        Please reach out to the Liquid Diamond team to build action categories.
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900">Pricing Recommendations</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          AI-driven pricing actions based on market analysis and inventory performance
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(data?.data || []).map((item) => (
          <ActionCard key={item.tag} item={item} />
        ))}
      </div>

      {(!data?.data || data.data.length === 0) && (
        <div className="text-center text-gray-400 py-20 text-sm">
          No pricing actions available.
        </div>
      )}
    </div>
  );
}
